import React from "react";
import { useSharedValue } from "../components/Constants/constants";
import { headerStyle, sectionStyle } from "./formConstants";
import { FormCheckBox, NormalFormDate, FormInput, FormTitle } from "./common";
import "./style.css";
import CountryDropdown from "./CountryDropdown";
import { useSelfCertIndividualForm } from "../Hooks/useSelfCertIndividualForm";
import { SelfCertIndividualService } from "../services/SelfCertIndividualService";

interface SelfCertFormProps {
  selectedRowData?: any;
  row?: any;
  selectedIndex?: number;
  onSaveSuccess?: (row: any) => void;
}

const SelfCertificationForm = ({
  selectedRowData,
  row,
  selectedIndex = 0,
  onSaveSuccess,
}: SelfCertFormProps) => {
  const { isMenuOpen } = useSharedValue();

  // Use the hook for form management
  const { model, updateField } = useSelfCertIndividualForm(selectedRowData);

  // Helper function to get field values from model
  const getFieldValue = (fieldName: string): any => {
    const value = model[fieldName] || "";
    return value;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    // Update the model using the hook
    updateField(name, fieldValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Use the model as the source of truth
      const currentFormValues = { ...model };
      currentFormValues.isMapDone = true;

      // Create save model with all form data
      const saveModel = new (model.constructor as any)(currentFormValues);

      // Save using the service
      const result = await SelfCertIndividualService.saveToApi(saveModel, row, selectedIndex);

      if (result.success) {
        if (onSaveSuccess) {
          onSaveSuccess(result.updatedRow);
        }
        alert("Form saved successfully");
      } else {
        console.error("❌ SelfCertIndividual Failed to save form:", result.error);
        alert("Failed to save form: " + result.error);
      }
    } catch (error) {
      console.error("❌ SelfCertIndividual Error saving form:", error);
      alert("Error saving form: " + error);
    }
  };

  return (
    <div className="scroll-container" style={headerStyle(isMenuOpen)}>
      <div
        style={{ width: "100%", display: "flex", flexDirection: "column", padding: 0, margin: 0 }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100% !important",
            minWidth: "100%",
            maxWidth: "100%",
            flex: "1 1 100%",
            display: "flex !important",
            flexDirection: "column",
            margin: "0 !important",
            padding: "0 !important",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
            {/* Section I: Identification of Individual */}
            <section style={sectionStyle}>
              <FormTitle value={"Section I – Identification of Individual"} />

              {/* UniqueIdentifier Field - Add at the very top */}
              {/* <section style={sectionStyle}> */}
              <FormInput
                label={"Unique Identifier"}
                id={"UniqueIdentifier"}
                name={"UniqueIdentifier"}
                value={getFieldValue("UniqueIdentifier")}
                onChange={handleChange}
              />
              {/* </section> */}

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Title"}
                    id={"SC-Individual-Title"}
                    name={"SC-Individual-Title"}
                    value={getFieldValue("SC-Individual-Title")}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: "3" }}>
                  <FormInput
                    label={"Last Name"}
                    id={"SC-Individual-Last_name"}
                    name={"SC-Individual-Last_name"}
                    value={getFieldValue("SC-Individual-Last_name")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"First Name"}
                    id={"SC-Individual-First_name"}
                    name={"SC-Individual-First_name"}
                    value={getFieldValue("SC-Individual-First_name")}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Middle Name"}
                    id={"SC-Individual-Middle_name"}
                    name={"SC-Individual-Middle_name"}
                    value={getFieldValue("SC-Individual-Middle_name")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Town/City of Birth"}
                    id={"SC-Individual-City_of_birth"}
                    name={"SC-Individual-City_of_birth"}
                    value={getFieldValue("SC-Individual-City_of_birth")}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <CountryDropdown
                    label={"Country of Birth"}
                    id={"SC-Individual-Country_of_birth"}
                    name={"SC-Individual-Country_of_birth"}
                    value={getFieldValue("SC-Individual-Country_of_birth")}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "SC-Individual-Country_of_birth",
                          value: e.target.value,
                          type: "select-one",
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <NormalFormDate
                    label={"Date of Birth (DD/MM/YYYY)"}
                    name={"SC-Individual-Date_of_birth"}
                    value={getFieldValue("SC-Individual-Date_of_birth")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <FormTitle value={"8. Current Residence Address"} />

              <FormInput
                label={"House/Apt/Suite Name/Number/Street"}
                id={"SC-Individual-Current_residence_address_street"}
                name={"SC-Individual-Current_residence_address_street"}
                value={getFieldValue("SC-Individual-Current_residence_address_street")}
                onChange={handleChange}
              />

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Town/City"}
                    id={"SC-Individual-Current_residence_address_city"}
                    name={"SC-Individual-Current_residence_address_city"}
                    value={getFieldValue("SC-Individual-Current_residence_address_city")}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Province/State"}
                    id={"SC-Individual-Current_residence_address_state"}
                    name={"SC-Individual-Current_residence_address_state"}
                    value={getFieldValue("SC-Individual-Current_residence_address_state")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <CountryDropdown
                    label={"Country"}
                    id={"SC-Individual-Current_residence_address_country"}
                    name={"SC-Individual-Current_residence_address_country"}
                    value={getFieldValue("SC-Individual-Current_residence_address_country")}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "SC-Individual-Current_residence_address_country",
                          value: e.target.value,
                          type: "select-one",
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Postal Code"}
                    id={"SC-Individual-Current_residence_address_postal"}
                    name={"SC-Individual-Current_residence_address_postal"}
                    value={getFieldValue("SC-Individual-Current_residence_address_postal")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <FormTitle value={"9. Mailing Address (If different)"} />

              <FormInput
                label={"House/Apt/Suite Name/Number/Street"}
                id={"SC-Individual-Mailing_address_street"}
                name={"SC-Individual-Mailing_address_street"}
                value={getFieldValue("SC-Individual-Mailing_address_street")}
                onChange={handleChange}
              />

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Town/City"}
                    id={"SC-Individual-Mailing_address_city"}
                    name={"SC-Individual-Mailing_address_city"}
                    value={getFieldValue("SC-Individual-Mailing_address_city")}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Province/State"}
                    id={"SC-Individual-Mailing_address_state"}
                    name={"SC-Individual-Mailing_address_state"}
                    value={getFieldValue("SC-Individual-Mailing_address_state")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <CountryDropdown
                    label={"Country"}
                    id={"SC-Individual-Mailing_address_country"}
                    name={"SC-Individual-Mailing_address_country"}
                    value={getFieldValue("SC-Individual-Mailing_address_country")}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "SC-Individual-Mailing_address_country",
                          value: e.target.value,
                          type: "select-one",
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Postal Code"}
                    id={"SC-Individual-Mailing_address_postal"}
                    name={"SC-Individual-Mailing_address_postal"}
                    value={getFieldValue("SC-Individual-Mailing_address_postal")}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </section>

            {/* Section II: Tax Residency */}
            <section style={sectionStyle}>
              <FormTitle value={"Section II – Tax Residency"} />

              <p style={{ marginBottom: 15 }}>
                I hereby certify that the Individual identified above is resident for tax purposes
                in the following jurisdiction(s) and the individual's Taxpayer Identification Number
                (TIN) or functional equivalent in its jurisdiction of residence for tax purposes is:
              </p>

              <CountryDropdown
                label={"Jurisdiction 1"}
                id={"SC-Individual-Tax_residency_country"}
                name={"SC-Individual-Tax_residency_country"}
                value={getFieldValue("SC-Individual-Tax_residency_country")}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "SC-Individual-Tax_residency_country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Tax reference number type"}
                    id={"SC-Individual-Tax_reference_number_type"}
                    name={"SC-Individual-Tax_reference_number_type"}
                    value={getFieldValue("SC-Individual-Tax_reference_number_type")}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Tax Identification Number"}
                    id={"SC-Individual-Tax_id"}
                    name={"SC-Individual-Tax_id"}
                    value={getFieldValue("SC-Individual-Tax_id")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", marginBottom: 15 }}>
                <FormCheckBox
                  label={
                    "Check this box if this jurisdiction does not issue or you are unable to procure a TIN or functional equivalent"
                  }
                  name={"SC-Individual-Tin_or_functional_equivalent"}
                  checked={getFieldValue("SC-Individual-Tin_or_functional_equivalent")}
                  onChange={handleChange}
                />
              </div>

              <FormInput
                label={"Specify the reason for non-availability of TIN"}
                id={"SC-Individual-Reason_for_non_tin"}
                name={"SC-Individual-Reason_for_non_tin"}
                value={getFieldValue("SC-Individual-Reason_for_non_tin")}
                onChange={handleChange}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 20,
                  marginTop: 10,
                  flexDirection: "row",
                }}
              >
                <p>
                  I certify that for the purposes of taxation, the Account Holder is not a tax
                  resident in any other country than Jurisdiction 1*
                </p>
                <div style={{ width: "6px" }} />
                <FormCheckBox
                  label={"Yes"}
                  name={"SC-Individual-Sole_residency_yes"}
                  checked={getFieldValue("SC-Individual-Sole_residency_yes")}
                  onChange={handleChange}
                />
                <div style={{ width: "6px" }} />
                <FormCheckBox
                  label={"No"}
                  name={"SC-Individual-Sole_residency_no"}
                  checked={getFieldValue("SC-Individual-Sole_residency_no")}
                  onChange={handleChange}
                />
              </div>

              <FormTitle
                value={"12. Additional Jurisdictions of Residency for Tax Purposes (if applicable)"}
              />

              {/* Table for additional jurisdictions */}
              <div style={{ overflowX: "auto", marginBottom: "20px" }}>
                <table
                  style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ddd" }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Jurisdiction
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Tax Identification Number
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Tax Reference Number Type
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Reason for non-availability of TIN (if applicable)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Row 1 - Jurisdiction 2 */}
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Jurisdiction 2"
                          name="sc_cp Jurisdiction 2"
                          value={getFieldValue("sc_cp Jurisdiction 2")}
                          onChange={handleChange}
                          placeholder="Select jurisdiction"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Tax Identification Number 2"
                          name="sc_cp Tax Identification Number 2"
                          value={getFieldValue("sc_cp Tax Identification Number 2")}
                          onChange={handleChange}
                          placeholder="Enter tax ID"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Tax Reference Number Type 2"
                          name="sc_cp Tax Reference Number Type 2"
                          value={getFieldValue("sc_cp Tax Reference Number Type 2")}
                          onChange={handleChange}
                          placeholder="Enter reference type"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Reason for non-availability of TIN 2"
                          name="sc_cp Reason for non-availability of TIN 2"
                          value={getFieldValue("sc_cp Reason for non-availability of TIN 2")}
                          onChange={handleChange}
                          placeholder="Enter reason if applicable"
                        />
                      </td>
                    </tr>
                    {/* Row 2 - Jurisdiction 3 */}
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Jurisdiction 3"
                          name="sc_cp Jurisdiction 3"
                          value={getFieldValue("sc_cp Jurisdiction 3")}
                          onChange={handleChange}
                          placeholder="Select jurisdiction"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Tax Identification Number 3"
                          name="sc_cp Tax Identification Number 3"
                          value={getFieldValue("sc_cp Tax Identification Number 3")}
                          onChange={handleChange}
                          placeholder="Enter tax ID"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Tax Reference Number Type 3"
                          name="sc_cp Tax Reference Number Type 3"
                          value={getFieldValue("sc_cp Tax Reference Number Type 3")}
                          onChange={handleChange}
                          placeholder="Enter reference type"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Reason for non-availability of TIN 3"
                          name="sc_cp Reason for non-availability of TIN 3"
                          value={getFieldValue("sc_cp Reason for non-availability of TIN 3")}
                          onChange={handleChange}
                          placeholder="Enter reason if applicable"
                        />
                      </td>
                    </tr>
                    {/* Row 3 - Jurisdiction 4 */}
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Jurisdiction 4"
                          name="sc_cp Jurisdiction 4"
                          value={getFieldValue("sc_cp Jurisdiction 4")}
                          onChange={handleChange}
                          placeholder="Select jurisdiction"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Tax Identification Number 4"
                          name="sc_cp Tax Identification Number 4"
                          value={getFieldValue("sc_cp Tax Identification Number 4")}
                          onChange={handleChange}
                          placeholder="Enter tax ID"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Tax Reference Number Type 4"
                          name="sc_cp Tax Reference Number Type 4"
                          value={getFieldValue("sc_cp Tax Reference Number Type 4")}
                          onChange={handleChange}
                          placeholder="Enter reference type"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Reason for non-availability of TIN 4"
                          name="sc_cp Reason for non-availability of TIN 4"
                          value={getFieldValue("sc_cp Reason for non-availability of TIN 4")}
                          onChange={handleChange}
                          placeholder="Enter reason if applicable"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section III: Declaration and Signature */}
            <section style={sectionStyle}>
              <FormTitle value={"Section III – Declaration and Signature"} />

              <FormInput
                label={"Signed Name"}
                id={"SC-Individual-Print_name"}
                name={"SC-Individual-Print_name"}
                value={getFieldValue("SC-Individual-Print_name")}
                onChange={handleChange}
              />

              <NormalFormDate
                label={"Date (DD/MM/YYYY)"}
                name={"SC-Individual-Date"}
                value={getFieldValue("SC-Individual-Date")}
                onChange={handleChange}
              />

              <FormInput
                label={
                  "If you have signed this on behalf of the Controlling Person please indicate the capacity in which you have acted here*"
                }
                id={"SC-Individual-Capacity"}
                name={"SC-Individual-Capacity"}
                value={getFieldValue("SC-Individual-Capacity")}
                onChange={handleChange}
              />

              <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                <input
                  type="checkbox"
                  id="SC-Individual-Signature_unreadable"
                  checked={!!getFieldValue("SC-Individual-Signature_unreadable")}
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "SC-Individual-Signature_unreadable",
                        type: "checkbox",
                        checked: e.target.checked,
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{ marginRight: 8 }}
                />
                <label htmlFor="SC-Individual-Signature_unreadable" style={{ fontWeight: 500 }}>
                  Check if signature is unreadable
                </label>
              </div>
            </section>

            <div
              style={{
                display: "flex",
                marginTop: 20,
                marginBottom: 40,
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                maxWidth: "100%",
                alignItems: "center",
                boxSizing: "border-box",
              }}
            >
              <button
                type="submit"
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#3B82F6",
                  color: "white",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginRight: "auto",
                  borderRadius: "0.375rem",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  cursor: "pointer",
                  border: "none",
                }}
              >
                Save Form
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SelfCertificationForm;
